// src/products/capRaise/pages/BookBuildPage.js
// BookBuild page with probability-weighted fundraising analysis

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Target
} from 'lucide-react';
import capRaiseApi from '../services/capRaiseApi';
import MagnitudeSelector, { useMagnitude } from '../../../components/ui/MagnitudeSelector';

const BookBuildPage = ({ selectedCompany, activeRound, user, addMessage }) => {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [magnitude, setMagnitude] = useState('millions');
  const [weightedTotal, setWeightedTotal] = useState(0);
  const [optimisticTotal, setOptimisticTotal] = useState(0);
  const [realisticTotal, setRealisticTotal] = useState(0);
  const [pessimisticTotal, setPessimisticTotal] = useState(0);
  
  const { toActualValue, getPlaceholder, getStepValue, label, formatCurrencyWithMagnitude } = useMagnitude(magnitude);

  // Load investors
  const loadInvestors = useCallback(async () => {
    if (!activeRound) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data: investorsData, error: investorsError } = await capRaiseApi.getInvestors(activeRound.id);
      if (investorsError) throw investorsError;
      
      // Initialize investors with probability fields if they don't exist
      const multiplier = magnitude === 'millions' ? 1000000 : magnitude === 'thousands' ? 1000 : 1;
      const investorsWithProbability = (investorsData || []).map(investor => {
        const investmentAmount = investor.investment_amount || investor.requested_amount || 0;
        return {
          ...investor,
          investment_amount: investmentAmount,
          investment_amount_display: multiplier > 1 ? investmentAmount / multiplier : investmentAmount,
          probability: investor.probability || 50, // Default 50% probability
          min_amount: investor.min_amount || 0,
          min_amount_display: multiplier > 1 ? (investor.min_amount || 0) / multiplier : (investor.min_amount || 0),
          max_amount: investor.max_amount || investmentAmount || 0,
          max_amount_display: multiplier > 1 ? (investor.max_amount || investmentAmount || 0) / multiplier : (investor.max_amount || investmentAmount || 0)
        };
      });
      
      setInvestors(investorsWithProbability);
    } catch (error) {
      console.error('Error loading investors:', error);
      addMessage?.('Error loading investors: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [activeRound, addMessage, magnitude]);

  // Calculate probability-weighted totals
  const calculateWeightedTotals = useCallback(() => {
    if (!investors.length) {
      setWeightedTotal(0);
      setOptimisticTotal(0);
      setRealisticTotal(0);
      setPessimisticTotal(0);
      return;
    }

    let weightedSum = 0;
    let optimisticSum = 0;
    let realisticSum = 0;
    let pessimisticSum = 0;

    investors.forEach(investor => {
      const amount = parseFloat(investor.investment_amount) || 0;
      const probability = parseFloat(investor.probability) || 0;
      const minAmount = parseFloat(investor.min_amount) || 0;
      const maxAmount = parseFloat(investor.max_amount) || amount || 0;

      // Weighted expected value: amount * probability
      const expectedValue = amount * (probability / 100);
      weightedSum += expectedValue;

      // Optimistic scenario: high probability investors at max amount
      if (probability >= 70) {
        optimisticSum += maxAmount;
        realisticSum += amount * (probability / 100);
        pessimisticSum += minAmount * (probability / 100);
      } else if (probability >= 40) {
        optimisticSum += maxAmount * 0.8;
        realisticSum += amount * (probability / 100);
        pessimisticSum += minAmount * (probability / 100);
      } else {
        optimisticSum += maxAmount * 0.5;
        realisticSum += amount * (probability / 100);
        pessimisticSum += minAmount * 0.5;
      }
    });

    setWeightedTotal(weightedSum);
    setOptimisticTotal(optimisticSum);
    setRealisticTotal(realisticSum);
    setPessimisticTotal(pessimisticSum);
  }, [investors]);

  useEffect(() => {
    loadInvestors();
  }, [loadInvestors]);

  // Update display values when magnitude changes
  useEffect(() => {
    const multiplier = magnitude === 'millions' ? 1000000 : magnitude === 'thousands' ? 1000 : 1;
    setInvestors(prev => prev.map(investor => ({
      ...investor,
      investment_amount_display: multiplier > 1 ? (investor.investment_amount || 0) / multiplier : (investor.investment_amount || 0),
      min_amount_display: multiplier > 1 ? (investor.min_amount || 0) / multiplier : (investor.min_amount || 0),
      max_amount_display: multiplier > 1 ? (investor.max_amount || 0) / multiplier : (investor.max_amount || 0)
    })));
  }, [magnitude]);

  useEffect(() => {
    calculateWeightedTotals();
  }, [calculateWeightedTotals]);

  // Handle investor field changes
  const handleInvestorFieldChange = async (investorId, field, value) => {
    try {
      let actualValue = parseFloat(value) || 0;
      
      // Convert display value to actual value for amount fields
      if (field === 'investment_amount' || field === 'min_amount' || field === 'max_amount') {
        actualValue = toActualValue(value);
        // Ensure value is positive (database constraint)
        if (actualValue < 0) {
          addMessage?.('Amount must be positive', 'error');
          return;
        }
        
        // Update local display value immediately
        setInvestors(prev => prev.map(inv => 
          inv.id === investorId ? { 
            ...inv, 
            [field]: actualValue,
            [`${field}_display`]: value 
          } : inv
        ));
      } else {
        // For probability, use the value directly
        actualValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
        setInvestors(prev => prev.map(inv => 
          inv.id === investorId ? { ...inv, [field]: actualValue } : inv
        ));
      }
      
      // Update database with actual value
      const updateData = { [field]: actualValue };
      await capRaiseApi.updateInvestor(investorId, updateData);
      
      addMessage?.('Investor updated successfully', 'success');
    } catch (error) {
      console.error('Error updating investor:', error);
      addMessage?.('Error updating investor: ' + (error.message || 'Invalid input'), 'error');
      // Reload to revert any local changes
      loadInvestors();
    }
  };

  const formatCurrency = (amount) => {
    return formatCurrencyWithMagnitude(amount);
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 70) return 'text-green-600 bg-green-50';
    if (probability >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getProbabilityLabel = (probability) => {
    if (probability >= 70) return 'High';
    if (probability >= 40) return 'Medium';
    return 'Low';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!activeRound) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Round</h3>
        <p className="text-gray-500">Create or select a fundraising round to manage book building.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">BookBuild</h1>
          <p className="text-gray-600">
            Probability-weighted fundraising analysis for <span className="font-medium">{activeRound.round_name}</span>
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadInvestors}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Probability-Weighted Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Target className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Weighted Total</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(weightedTotal)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Expected value</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-green-200">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Optimistic</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(optimisticTotal)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Best case scenario</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Realistic</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(realisticTotal)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Most likely</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-red-200">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-red-600 rotate-180" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pessimistic</p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(pessimisticTotal)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Worst case scenario</p>
            </div>
          </div>
        </div>
      </div>

      {/* Target Comparison */}
      {activeRound.target_amount && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">Target Raise</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(activeRound.target_amount)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-blue-900">Coverage</p>
              <p className={`text-xl font-bold ${
                weightedTotal >= activeRound.target_amount ? 'text-green-600' : 'text-red-600'
              }`}>
                {((weightedTotal / activeRound.target_amount) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
          {weightedTotal < activeRound.target_amount && (
            <div className="mt-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Shortfall: {formatCurrency(activeRound.target_amount - weightedTotal)}
            </div>
          )}
        </div>
      )}

      {/* Investors Table */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Investor Probability Analysis</h3>
              <p className="text-sm text-gray-600 mt-1">
                Set probability of investment and amount ranges for each investor to calculate weighted totals
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Amount Units:</span>
              <MagnitudeSelector value={magnitude} onChange={setMagnitude} />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investment Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Probability</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min / Max Range</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {investors.map((investor) => {
                const expectedValue = (parseFloat(investor.investment_amount) || 0) * ((parseFloat(investor.probability) || 0) / 100);
                const probabilityClass = getProbabilityColor(parseFloat(investor.probability) || 0);
                
                return (
                  <tr key={investor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{investor.name}</div>
                        <div className="text-sm text-gray-500">{investor.email}</div>
                        {investor.company && (
                          <div className="text-xs text-gray-400">{investor.company}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500">{label ? `$${label}` : '$'}</span>
                        <input
                          type="number"
                          value={investor.investment_amount_display || ''}
                          onChange={(e) => handleInvestorFieldChange(investor.id, 'investment_amount', e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          placeholder={getPlaceholder(5000000)}
                          step={getStepValue()}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={investor.probability || ''}
                          onChange={(e) => handleInvestorFieldChange(investor.id, 'probability', e.target.value)}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          placeholder="50"
                        />
                        <span className="text-xs text-gray-500">%</span>
                        <span className={`px-2 py-1 text-xs rounded font-medium ${probabilityClass}`}>
                          {getProbabilityLabel(parseFloat(investor.probability) || 0)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(expectedValue)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {((parseFloat(investor.probability) || 0) / 100 * 100).toFixed(0)}% of {formatCurrency(parseFloat(investor.investment_amount) || 0)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          value={investor.min_amount_display || ''}
                          onChange={(e) => handleInvestorFieldChange(investor.id, 'min_amount', e.target.value)}
                          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Min"
                          step={getStepValue()}
                        />
                        <span className="text-gray-400 text-xs">/</span>
                        <input
                          type="number"
                          value={investor.max_amount_display || ''}
                          onChange={(e) => handleInvestorFieldChange(investor.id, 'max_amount', e.target.value)}
                          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Max"
                          step={getStepValue()}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr className="border-t-2 border-gray-300">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  TOTAL
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                  {formatCurrency(investors.reduce((sum, inv) => sum + (parseFloat(inv.investment_amount) || 0), 0))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                  {(investors.reduce((sum, inv) => sum + (parseFloat(inv.probability) || 0), 0) / investors.length || 0).toFixed(1)}% avg
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                  {formatCurrency(weightedTotal)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  Weighted Expected Value
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {investors.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No investors found</h3>
            <p className="mt-1 text-sm text-gray-500">Add investors in the Investors section first.</p>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-yellow-900 mb-2">💡 How Probability-Weighted BookBuild Works</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• <strong>Investment Amount:</strong> The potential investment amount from each investor</li>
          <li>• <strong>Probability:</strong> Your estimate of likelihood (0-100%) that this investor will commit</li>
          <li>• <strong>Expected Value:</strong> Investment Amount × Probability = Weighted contribution</li>
          <li>• <strong>Weighted Total:</strong> Sum of all expected values gives realistic fundraising projection</li>
          <li>• <strong>Min/Max Range:</strong> Optional range for scenario analysis (optimistic/pessimistic)</li>
        </ul>
        <p className="text-xs text-yellow-700 mt-3">
          <strong>Realistic approach:</strong> Most startups find it difficult to raise capital. This model accounts for uncertainty by using probabilities rather than assuming all investors will commit.
        </p>
      </div>
    </div>
  );
};

export default BookBuildPage;